import React from "react";
import styled from "styled-components";

import {
  brand_orange,
  brand_orange_deep,
  narrowScreenMediaQuery,
} from "../constants/styles";

// Accessible single-select dropdown with keyword filtering.
//
// Hand-rolled rather than pulling in react-select: the rebrand drives dark mode
// entirely through CSS (`[data-theme="dark"] &`), which an emotion-in-JS
// library cannot see without plumbing the theme through props, and every other
// control on these pages is a styled-component.
//
// Implements the ARIA 1.2 combobox-with-listbox-popup pattern:
//   - the search field is role="combobox" with aria-expanded / aria-controls
//   - the visible option list is role="listbox", each row role="option"
//   - the focused row is tracked via aria-activedescendant (DOM focus stays in
//     the input, so typing keeps working while arrowing)
//
// Keyboard: ArrowUp/Down move, Home/End jump, Enter selects, Escape closes and
// returns focus to the trigger, Tab closes.
//
// `options` is [{ value, label, count? }]. `value` is the selected option's
// value ("" means none). Search is opt-in above SEARCH_THRESHOLD options so a
// 3-item list doesn't get a pointless search box.

const SEARCH_THRESHOLD = 8;
const MAX_RENDERED = 200;

// `showAll` prepends a "clear the filter" row and enables the × button. Turn it
// off for controls that always hold a value (e.g. sort order), otherwise the
// list shows a duplicate of whichever option is the default.
export default function SearchableSelect({
  value = "",
  onChange,
  options = [],
  allLabel = "Semua",
  showAll = true,
  placeholder = "Pilih…",
  searchPlaceholder = "Ketik untuk mencari…",
  ariaLabel,
  id,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);

  const wrapRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const reactId = React.useId();
  const baseId = id || `searchable-select-${reactId}`;
  const listboxId = `${baseId}-listbox`;

  // "Semua …" is always the first row so clearing is one click, never a hunt.
  const allOption = React.useMemo(
    () => ({ value: "", label: allLabel }),
    [allLabel],
  );

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? options.filter((o) => o.label.toLowerCase().includes(needle))
      : options;
    const head = showAll ? [allOption] : [];
    return [...head, ...matches.slice(0, MAX_RENDERED)];
  }, [options, query, allOption, showAll]);

  const overflowCount = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    const total = needle
      ? options.filter((o) => o.label.toLowerCase().includes(needle)).length
      : options.length;
    return Math.max(0, total - MAX_RENDERED);
  }, [options, query]);

  const selected = options.find((o) => o.value === value) || null;
  const showSearch = options.length > SEARCH_THRESHOLD;

  const close = React.useCallback((refocus = true) => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    if (refocus && triggerRef.current) triggerRef.current.focus();
  }, []);

  const commit = React.useCallback(
    (option) => {
      if (option) onChange(option.value);
      close();
    },
    [onChange, close],
  );

  // Close on outside pointer-down (not click: a mousedown that starts inside
  // and ends outside shouldn't dismiss).
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  React.useEffect(() => {
    if (open && showSearch && inputRef.current) inputRef.current.focus();
  }, [open, showSearch]);

  // Keep the active row in view while arrowing.
  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(filtered.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        commit(filtered[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
      case "Tab":
        close(false);
        break;
      default:
        break;
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        ref={triggerRef}
        id={baseId}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        $active={Boolean(selected)}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={showSearch ? undefined : onKeyDown}
      >
        <TriggerLabel $placeholder={!selected}>
          {selected ? selected.label : placeholder}
        </TriggerLabel>
        {selected && showAll ? (
          <ClearButton
            as="span"
            role="button"
            tabIndex={0}
            aria-label={`Hapus filter ${ariaLabel || ""}`.trim()}
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }
            }}
          >
            ×
          </ClearButton>
        ) : (
          <Caret aria-hidden="true" $open={open}>
            ▾
          </Caret>
        )}
      </Trigger>

      {open && (
        <Panel>
          {showSearch && (
            <SearchRow>
              <SearchInput
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  filtered.length > 0
                    ? `${baseId}-opt-${activeIndex}`
                    : undefined
                }
                aria-label={searchPlaceholder}
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
              />
            </SearchRow>
          )}

          <List
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            tabIndex={-1}
          >
            {filtered.length === 0 && (
              <EmptyRow role="status">Tidak ada hasil.</EmptyRow>
            )}
            {filtered.map((option, idx) => (
              <Option
                key={option.value || "__all__"}
                id={`${baseId}-opt-${idx}`}
                data-idx={idx}
                role="option"
                aria-selected={option.value === value}
                $active={idx === activeIndex}
                $selected={option.value === value}
                // Mouse: select on click, and mirror hover into the active row
                // so keyboard and pointer never disagree.
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(option)}
              >
                <OptionLabel>{option.label}</OptionLabel>
                {typeof option.count === "number" && (
                  <OptionCount>{option.count}</OptionCount>
                )}
              </Option>
            ))}
            {overflowCount > 0 && (
              <EmptyRow role="status">
                +{overflowCount} lainnya — persempit pencarian.
              </EmptyRow>
            )}
          </List>
        </Panel>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 240px;
  max-width: 100%;

  @media ${narrowScreenMediaQuery} {
    width: 100%;
  }
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $active }) => ($active ? brand_orange_deep : "#d1d5db")};
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${brand_orange};
  }
  &:focus-visible {
    outline: none;
    border-color: ${brand_orange};
    box-shadow: 0 0 0 3px rgba(239, 135, 38, 0.15);
  }

  [data-theme="dark"] & {
    background: #0f172a;
    border-color: ${({ $active }) =>
      $active ? brand_orange_deep : "rgba(255, 255, 255, 0.14)"};
    color: #f3f4f6;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder }) => ($placeholder ? "#9ca3af" : "inherit")};
`;

const Caret = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: #6b7280;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "none")};
  transition: transform 150ms ease;
`;

const ClearButton = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  color: #4b5563;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${brand_orange_deep};
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.12);
    color: #e5e7eb;
  }
`;

const Panel = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  /* Wider than the trigger on purpose: vocabulary names are long, and the
   * popup is absolutely positioned so it costs the layout nothing.
   */
  width: max-content;
  min-width: 100%;
  max-width: min(420px, calc(100vw - 48px));
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
  overflow: hidden;

  [data-theme="dark"] & {
    background: #17171e;
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
  }
`;

const SearchRow = styled.div`
  padding: 10px;
  border-bottom: 1px solid #f0f1f3;

  [data-theme="dark"] & {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  color: #1f2937;

  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    outline: none;
    border-color: ${brand_orange};
    box-shadow: 0 0 0 3px rgba(239, 135, 38, 0.15);
  }

  [data-theme="dark"] & {
    background: #0f172a;
    border-color: rgba(255, 255, 255, 0.14);
    color: #f3f4f6;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 280px;
  overflow-y: auto;
`;

const Option = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  color: ${({ $selected }) => ($selected ? brand_orange_deep : "#374151")};
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  background: ${({ $active }) =>
    $active ? "rgba(239,135,38,0.10)" : "transparent"};

  [data-theme="dark"] & {
    color: ${({ $selected }) => ($selected ? "#fdba74" : "#d1d5db")};
    background: ${({ $active }) =>
      $active ? "rgba(239,135,38,0.18)" : "transparent"};
  }
`;

const OptionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionCount = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 1px 8px;

  [data-theme="dark"] & {
    color: #9ca3af;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const EmptyRow = styled.li`
  padding: 12px 10px;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
`;
